import { GridViewView } from '../view/GridViewView';
import { templateGridView } from '../view/template';
import {
  BaseWebComponentController,
  CustomEventListener,
  IInitialSetup,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { GridViewWebComponent } from '..';
import { GridColumnPreset, GridViewMetadata, SelectionOptions, ViewType } from '../types';
import { ColumnType, FilterEvents, GridEvents } from '../../types/common';
import { getColumnKeyForFilter, isDefaultFilter, sortFiltersByColumnKey } from '../../utils/filters';
import { GridSettings, GridSettingsEvents } from '../../gridSettings/types';
import { GridFiltersWebComponent } from '../../gridFilters';
import { GridFiltersController } from '../../gridFilters/controller/GridFiltersController';
import { TemplateState } from '../../../types/common/types';
import { GridFooterWebComponent } from '../../gridFooter';
import { GridFooterController } from '../../gridFooter/controller/GridFooterController';
import { GridFooterView } from '../../gridFooter/view/GridFooterView';
import { smoothScroll } from '../../../utils/scroll';
import { Filter } from '../../gridFilters/types';
import { areArraysEqual, isEmpty } from '@nirvana/shared/basic-types/object';
import { GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { GzTooltip } from '../../../ui/gzTooltip';
import { GzButtonState } from '../../../ui/gzButton/types';
import { FormValidator } from '../../../ui/gzForm/formField/types/form-validator';
import { GridCellEditWebComponent } from '../../uiElements/gridCellEdit';
import { GridTitleConfig } from '../../uiElements/gridViewTitle/types';
import { SelectExtraOptionsEvents } from '../../uiElements/gridSelectionExtraOptions/types';
import { SelectionStore } from '../component/SelectionStore';
import { GridSelectionColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-selection-column';
import { GridItem } from '@vaadin/vaadin-grid';
import { DOMEventDispatcher } from '../../../utils/event.utils';
import { isTargetActionable, toggleActive } from '../../../utils/htmlElement.utils';
import { SmartViewEvents } from '../../genericGridSmartViews/types';
import { State as GridSummaryState } from '../../uiElements/gridSummary/types';
import { DATA_LOAD_MORE } from '../../utils/constants';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { DropdownMenuEvents } from '../../../ui/gzDropdownMenu/types';
import { onRowRightClickEvent } from '../../utils/rowRightClickEvent';

const isEqual = require('lodash.isequal');

export class GridViewController<
  Column extends number,
  ColumnKeys extends string,
  GridItemType = any
> extends BaseWebComponentController {
  private static readonly MAX_GRID_SIZE = 500;

  public component: GridViewWebComponent<Column, ColumnKeys>;
  public view: GridViewView<Column, ColumnKeys>;
  public viewConstructor: typeof GridViewView;
  private numPagesLoadedOnInfiniteScroll = 0;
  private shouldLoadPageOnScroll: boolean;
  public numItems: number;
  private gridConfigsSet = false;
  private bodyRef: HTMLBodyElement;
  private bodyEventListeners: { eventType: string; callback: CustomEventListener }[];
  private noRowsPrevFetched = -1;
  private selectAllEnabled = false;
  private selectionStore: SelectionStore<GridItemType>;
  private hasAnyOpenDropdown = false;

  /**
   * @private
   * This flag is used to prevent multiple calls of the `selectRows` event, which is triggered each time the `selectedItems` property changes on the `vaadin-grid` component.
   */
  private rowSelectEventRunning = false;

  constructor(component: GridViewWebComponent<Column, ColumnKeys>) {
    super(component);
    this.initialSetup({
      viewConstructor: GridViewView,
      viewTemplate: templateGridView,
    });
  }

  public getSelectAllEnabled(): boolean {
    return !!this.selectAllEnabled;
  }

  initialSetup(config: IInitialSetup) {
    super.initialSetup(config);
    this.bodyRef = window.document.querySelector('body') as HTMLBodyElement;
    this.bodyEventListeners = [];
  }

  disconnected() {
    super.disconnected();
    this.bodyEventListeners.forEach(eventListener => {
      this.bodyRef.removeEventListener(eventListener.eventType, eventListener.callback);
    });
  }

  prepareListeners() {
    const addedEventListeners = super.prepareListeners();

    addedEventListeners.forEach(eventListener => {
      if (eventListener.element === this.bodyRef) {
        this.bodyEventListeners.push({ eventType: eventListener.event, callback: eventListener.callback });
      }
    });

    return addedEventListeners;
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.GRID_STARTED,
        callback: () => this.handleGridItemsLoad(),
      },
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.LOAD_MORE_ROWS,
        callback: event => this.loadPageOnInfiniteScroll(event.detail.numRecordsToFetch, true),
      },
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.LOAD_FIRST_PAGE,
        callback: this.loadFirstPage,
      },
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.LOAD_PREVIOUS_PAGE,
        callback: this.loadPreviousPage,
      },
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.LOAD_NEXT_PAGE,
        callback: this.loadNextPage,
      },
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.LOAD_LAST_PAGE,
        callback: this.loadLastPage,
      },
      {
        element: this.view.dom.$gridFilters,
        event: FilterEvents.FILTERS_UPDATED,
        callback: this.handleFiltersUpdatedEvent,
      },
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.GRID_SETTINGS_CREATED,
        callback: this.onGridSettingsCreated,
      },
      {
        element: this.view.dom.$toggleFiltersVisibility,
        event: 'click',
        callback: this.onToggleFiltersBtnClick,
      },
      {
        element: this.view.dom.$toolbarActionsRefresh,
        event: 'click',
        callback: () => this.handleGridItemsLoad(true),
      },
      {
        element: this.view.dom.$wrapper,
        event: GridEvents.REFRESH_DATA_FROM_FOOTER,
        callback: this.handleRefreshDataFromFooter,
      },
      {
        element: this.view.dom.$gridFooter,
        event: GridEvents.BACK_TO_TOP,
        callback: this.backToTop,
      },
      {
        element: this.view.dom.$gridFooter,
        event: GridEvents.SET_PAGE_SIZE,
        callback: this.setPageSize,
      },
      {
        element: this.view.dom.$gridFooter,
        event: GridEvents.SET_PAGE_NUMBER,
        callback: this.setPageNumber,
      },
      {
        element: this.view.dom.$gridFooter,
        event: GridEvents.SET_PAGE_NUMBER_WITHOUT_RELOADING,
        callback: this.setPageNumberWithoutReloading,
      },
      {
        element: this.view.gridTable,
        event: 'scroll',
        callback: this.loadMoreWhenScrolledToBottom,
      },
      {
        element: this.view.dom.$grid,
        event: 'selected-items-changed',
        callback: this.handleRowSelectEvent,
      },
      {
        element: this.view.dom.$grid,
        event: 'column-reorder',
        callback: this.handleColumnReorderEvent,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.GRID_SORTED,
        callback: this.handleGridSorted,
      },
      {
        element: this.view.gridTable,
        event: 'scroll',
        callback: this.handleGridTableScrolling,
      },
      {
        element: this.view.dom.$grid,
        event: GridViewView.GRID_COLUMN_RESIZE,
        callback: this.updateGridColumnSizes,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EDIT_ROW,
        callback: this.onEditGridRow,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EDIT_ROW_SAVE,
        callback: this.onEditGridRowSave,
      },
      {
        element: this.view.gridTable,
        event: GridEvents.EDIT_ROW_CANCEL,
        callback: this.onEditGridRowCancel,
      },
      {
        element: this.view.gridTable,
        event: 'click',
        callback: this.handleRowClickEvents,
      },
      {
        element: this.view.dom.$csvExportButton,
        event: 'click',
        callback: this.handleCSVExport,
      },
      {
        element: this.view.dom.$saveViewButton,
        event: 'click',
        callback: this.emitSaveViewPropsSavedEvent,
      },
      {
        element: this.view.dom.$saveViewAsButton,
        event: 'click',
        callback: this.emitShowSaveViewAsDialogEvent,
      },
      {
        element: this.view.dom.$discardViewChanges,
        event: 'click',
        callback: this.discardViewChanges,
      },
      {
        element: this.view.dom.$favoriteIcon,
        event: 'click',
        callback: this.emitSmartViewFavoriteChange,
      },
      {
        element: this.view.dom.$grid,
        event: SelectExtraOptionsEvents.EXTRA_OPTION_SELECTION_CHANGE,
        callback: this.handleSelectExtraOptionsChange,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: this.handleRowsLoadingFinished,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.GRID_SELECT_ALL,
        callback: this.handleSelectionAll,
      },
      {
        element: this.view.dom.$grid,
        event: DropdownMenuEvents.OPEN,
        callback: this.handleDropdownOpen,
      },
      {
        element: this.view.dom.$grid,
        event: DropdownMenuEvents.CLEAR,
        callback: this.handleDropdownClose,
      },
      {
        element: this.view.dom.$grid,
        event: 'contextmenu',
        callback: event => onRowRightClickEvent<GridItemType>(this, event),
      },
    ];
  }

  public setValidatorsOnRowAndColumn(rowIndex: number, columnKey: string, validators: FormValidator[]): void {
    // In the table HTML element the row count starts at 1 and 1st row is the table header, so we need to start from
    // index 2
    const editedCells = this.view.getAllEditedCellsInRow(rowIndex + 2);

    editedCells.forEach((gridCellEdit: GridCellEditWebComponent) => {
      const cellColumnKey = gridCellEdit.getAttribute('id');

      if (cellColumnKey === columnKey) {
        gridCellEdit.formField.Validators = validators;
      }
    });
  }

  public disableDataLoading(disabled: boolean) {
    this.shouldLoadPageOnScroll = !disabled;
    this.view.setDisabledFooter(disabled);
    this.view.setDisabledToolbar(disabled);
    this.view.setDisabledGlobalActions(disabled);
    this.view.setDisabledFilters(disabled);
  }

  public setMoreItemsLoadingState(showLoadingState: boolean) {
    showLoadingState
      ? this.view.setElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE)
      : this.view.removeElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE);
  }

  public setLoadingState(loading: boolean) {
    this.disableDataLoading(loading);
    this.setMoreItemsLoadingState(loading);
  }

  /**
   *
   * @param {boolean} resetSelectionStore
   * @param {boolean} forceResetSelectionStore set this parameter when call function after taking bulk actions on grid items,
   * such as delete after pressing select all, or else unexpected behaviors can occur, such as infinite trigger on SELECT_ROWS event
   *
   * @returns {Promise<void>}
   */
  public async handleGridItemsLoad(resetSelectionStore = false, forceResetSelectionStore = false) {
    this.component.gridLoaded = false;
    this.updateCsvButtonState(GzButtonState.DISABLED);
    this.numPagesLoadedOnInfiniteScroll = 0;
    this.view.dom.$grid.items = [];
    this.noRowsPrevFetched = -1;
    this.view.disableAllGridRowsEditMode();

    if (!this.component.hideFooter) {
      this.view.dom.$gridFooter.controller.setDisabledForFetchingDataButtons(true);
    }

    if (this.component.hasSelectableRows) {
      if (forceResetSelectionStore) {
        this.selectAllEnabled = false;
      }
      if (!this.selectAllEnabled) {
        this.view.dom.$grid.selectedItems = [];
        if (resetSelectionStore) {
          this.selectionStore.removeAll();
        }
      }
    }

    await this.executeDataLoading(async () => {
      const gridItemsToLoad = this.component.hideFooter ? GridViewController.MAX_GRID_SIZE : this.component.pageSize;

      if (this.component.shouldLoadAsync) {
        await this.loadGridAsync(gridItemsToLoad);
      } else {
        await this.loadGridSync(gridItemsToLoad);
      }
    });

    this.component.dispatchEvent(
      new CustomEvent(GridEvents.GRID_LOADED, {
        detail: {},
        bubbles: true,
      })
    );

    this.view.setHeightByRows(this.component.dynamicHeight);
  }

  public setTitleProps(config: GridTitleConfig, noEscapeSpaceInTitle: boolean): void {
    this.view.setTitleConfig(config);
    this.view.setTitleNoEscape(noEscapeSpaceInTitle);
  }

  private async loadPageOnInfiniteScroll(numRecordsToFetch: number, scrollFirstFewItems: boolean) {
    // If already loaded all items, do nothing.
    const numCurrentItems = (this.view.dom.$grid.items || []).length;
    this.view.disableAllGridRowsEditMode();

    if (numCurrentItems % 50 !== 0) {
      this.disableDataLoading(false);
      return;
    }

    await this.executeDataLoading(async () => {
      await this.fetchAndAppendRowsToGrid(numRecordsToFetch, scrollFirstFewItems);
    });
  }

  public async loadFirstPage() {
    this.component.pageNumber = 1;
    await this.handleGridItemsLoad();
    if (this.selectionStore) {
      this.view.dom.$grid.selectedItems = this.selectionStore.getItemsIntersection(this.view.dom.$grid.items);
    }
  }

  private async loadPreviousPage() {
    if (this.showingItemsFromFirstPage()) {
      // There are no more pages to load
      return;
    }

    this.component.pageNumber--;
    await this.handleGridItemsLoad();
    if (this.selectionStore) {
      this.view.dom.$grid.selectedItems = this.selectionStore.getItemsIntersection(this.view.dom.$grid.items);
    }
  }

  private async loadNextPage() {
    if (this.showingItemsFromLastPage()) {
      // There are no more pages to load
      return;
    }

    this.component.pageNumber++;
    await this.handleGridItemsLoad();
    if (this.selectionStore) {
      this.view.dom.$grid.selectedItems = this.selectionStore.getItemsIntersection(this.view.dom.$grid.items);
    }
  }

  private async loadLastPage() {
    this.component.pageNumber = Math.ceil(this.numItems / this.component.pageSize);
    await this.handleGridItemsLoad();
    if (this.selectionStore) {
      this.view.dom.$grid.selectedItems = this.selectionStore.getItemsIntersection(this.view.dom.$grid.items);
    }
  }

  private async executeDataLoading(dataLoadingFn: () => Promise<void>) {
    this.view.resetTemplateState(this.view.dom.$wrapper);
    this.disableDataLoading(true);

    try {
      const onDataLoadingStartedFn = this.component.onDataLoadingStarted;
      if (onDataLoadingStartedFn) {
        await onDataLoadingStartedFn();
      }

      await dataLoadingFn();

      const onDataLoadingFinishedWithSuccessFn = this.component.onDataLoadingFinishedWithSuccess;
      if (onDataLoadingFinishedWithSuccessFn) {
        await onDataLoadingFinishedWithSuccessFn();
      }
    } catch (e) {

      if (this.view.dom.$wrapper) {
        this.view.setTemplateState(this.view.dom.$wrapper, TemplateState.ERROR);
      }

      const onDataLoadingFinishedWithErrorFn = this.component.onDataLoadingFinishedWithError;
      if (onDataLoadingFinishedWithErrorFn) {
        await onDataLoadingFinishedWithErrorFn(e);
      }
    }

    if (!this.view.dom.$grid) {
      return;
    }
    this.disableDataLoading(false);
    DOMEventDispatcher.dispatchLocalEvent(this.view.dom.$grid, GridEvents.ROWS_LOADING_FINISHED);
  }

  private async fetchAndUpdateSummary() {
    if (this.component.hideFooter) {
      return;
    }

    this.view.dom.$gridFooter.gridSummary = '';
    this.view.dom.$gridFooter.controller.updateGridSummaryState(GridSummaryState.LOADING);

    const response = await this.component.dataProvider.getSummary({
      filters: this.component.filters,
      extraParams: this.component.extraParams || {},
      pageSize: this.component.pageSize,
      pageNumber: this.component.pageNumber,
      sortColumnName: this.component.sortColumnName,
      sortDirection: this.component.sortDirection,
    });

    if (!this.view.dom.$gridFooter) {
      return;
    }
    this.view.dom.$gridFooter.gridSummary = response.summary;
    this.numItems = response.numItems;
    this.component.gridLoaded = true;

    if (response.extraDetails) {
      this.setSummaryExtraDetailsForView(response.extraDetails);
    } else {
      this.setSummaryExtraDetailsForView(null);
    }

    this.view.dom.$gridFooter.numTotalItems = this.numItems;
    this.view.dom.$gridFooter.numTotalPages = Math.ceil(this.numItems / this.component.pageSize);
    await this.view.dom.$gridFooter.controller.render(); // re-render the footer
    this.view.dom.$gridFooter.controller.updateGridSummaryState(null);
  }

  private setSummaryExtraDetailsForView(extraDetails) {
    this.view.gridSummaryExtraDetails = extraDetails;
  }

  public getSummaryExtraDetailsForView() {
    return this.view.gridSummaryExtraDetails;
  }

  public updateNoResultsMessage(message) {
    this.view.dom.$noResultsMessage.innerHTML = message;
  }

  private async fetchAndAppendRowsToGrid(numRecordsToFetch: number, scrollFirstFewItems?: boolean): Promise<void> {
    this.view.setElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE);
    await this.view.smoothScrollGridToBottom();

    const numCurrentItems = (this.view.dom.$grid.items || []).length;

    let responseCount = false;
    try {
      if (this.noRowsPrevFetched === 0) {
        return;
      }

      const response = await this.component.dataProvider.getRows({
        columns: this.component.columns,
        filters: this.component.filters,
        extraParams: this.component.extraParams || {},
        numRecordsToSkip: numCurrentItems,
        numRecordsToFetch: numRecordsToFetch,
      });

      const rawEventsArray = (response.rawEvents as Array<{ [key: string]: string }>) || [];
      responseCount = !!rawEventsArray.length;
      this.noRowsPrevFetched = rawEventsArray.length;
      // Increase the number of pages only in case of success. If there's an error, fetch the same
      // page again.
      this.numPagesLoadedOnInfiniteScroll++;

      this.view.appendRowsToGrid(rawEventsArray);
    } finally {
      this.view.removeElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE);
      this.updateCsvButtonState(responseCount ? GzButtonState.ENABLED : GzButtonState.DISABLED);

      // When loading the first page, set the scroll to the top. For next pages, scroll a little bit
      // to first few elements in the page.
      if (this.numPagesLoadedOnInfiniteScroll === 1) {
        this.view.dom.$grid.scrollToIndex(0);
      } else {
        if (scrollFirstFewItems) {
          await smoothScroll(this.view.dom.$grid, {
            scrollEnd: this.view.dom.$grid._scrollTop + 150,
            initialDelayTimeInMs: 500,
            intervalTimeInMs: 50,
            percentage: 0.1,
            steps: 20,
          });
        }
      }
      if (!this.component.hideFooter) {
        const disableLoadMoreButton = this.showingAllItemsOnSinglePage();
        this.view.dom.$gridFooter.numTotalItems = this.numItems;
        this.view.dom.$gridFooter.numTotalPages = Math.ceil(this.numItems / this.component.pageSize);
        this.view.dom.$gridFooter.controller.setDisabledLoadMore(disableLoadMoreButton);
      }
    }
  }

  private async fetchAndOverwriteRowsInGrid(): Promise<void> {
    if (!this.view.dom.$wrapper) {
      return;
    }
    this.view.setElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE);

    this.view.scrollGridToTop();

    this.component.pageNumber = Math.max(1, this.component.pageNumber); // prepare to request page bigger than 0

    let responseCount = false;
    let response;
    try {
      response = await this.component.dataProvider.getRows({
        columns: this.component.columns,
        filters: this.component.filters,
        extraParams: this.component.extraParams || {},
        pageNumber: this.component.pageNumber,
        pageSize: this.component.pageSize,
        sortColumnName: this.component.sortColumnName,
        sortDirection: this.component.sortDirection,
      });

      const rawEventsArray = (response.rawEvents as Array<{ [key: string]: string }>) || [];
      responseCount = !!rawEventsArray.length;

      this.view.overwriteRowsInGrid(rawEventsArray, this.selectAllEnabled);

      if (!responseCount) {
        this.component.pageNumber = 0; // display 0 in current page selector
      }
    } finally {
      this.view.removeElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE);
      this.updateCsvButtonState(responseCount ? GzButtonState.ENABLED : GzButtonState.DISABLED);

      this.view.scrollGridToTop();

      if (!this.component.hideFooter) {
        this.view.dom.$gridFooter.controller.setDisabledForFetchingDataButtons(!responseCount);
        this.view.dom.$gridFooter.pageNumber = this.component.pageNumber;
        this.view.dom.$gridFooter.pageSize = this.component.pageSize;
        this.view.dom.$gridFooter.numSelectedItems = this.component.selectedItems?.length ?? 0;
        this.view.dom.$gridFooter.numTotalItems = this.numItems;
        this.view.dom.$gridFooter.numTotalPages = Math.ceil(this.numItems / this.component.pageSize);

        await this.view.dom.$gridFooter.controller.render(); // re-render the footer
      }

      DOMEventDispatcher.dispatchGlobalEvent(this.view.dom.$wrapper, GridEvents.ROWS_LOADING_FINISHED, {
        loadedItems: response?.rawEvents?.length,
      });
    }
  }

  public async getDataForView(): Promise<GridViewMetadata<Column, ColumnKeys> | undefined> {
    const {
      viewId,
      allColumnDetails,
      columns,
      filterDependencyMapping,
      filters,
      predefinedFiltersKeys,
      isCompactViewChecked,
      defaultViewProps,
      pageSize,
      pageNumber,
      headerRenderers,
      columnRenderers,
      rowDetailsRenderer,
      dataProvider,
      removeFilters,
      hideResetFilters,
      hideHeaderActions,
      hideColumnHeaders,
      hideFooter,
      hasSelectableRows,
      hideGridRefresh,
      hideFiltersToggle,
      disableGridSettings,
      inlineRowActions,
      columnConfigs,
      defaultFlexGrowColumn,
      customStrings,
      enableRowClickEvent,
      enableCSVExport,
      showFilters,
      smartViewsEnabled,
      smartViewDiscardButtonDisabledState,
      smartViewSaveButtonDisabledState,
      titleConfig,
      hasPagination,
      pageSizeOptions,
      noEscapeSpaceInTitle,
      cellClassNameGenerator,
      hasSelectionExtraOptions,
      sortColumnName,
      sortDirection,
      filtersUiConfigs,
      favoriteIconConfig,
    } = this.component;

    const fields = [viewId, allColumnDetails, columns, isCompactViewChecked];
    const numEmpty = fields.map(field => isEmpty(field)).filter(value => value).length;

    this.view.customStrings = customStrings;

    if (!this.gridConfigsSet) {
      this.setGridViewConfigs();
    }

    if (numEmpty === 0) {
      if (hasSelectableRows) {
        if (!this.selectionStore) {
          this.selectionStore = new SelectionStore<GridItemType>(this.component.selectionStoreConfig);
        } else {
          this.selectionStore.removeAll();
        }
      }

      return {
        viewId,
        allColumnDetails,
        columns,
        filterDependencyMapping,
        filters,
        predefinedFiltersKeys,
        isCompactViewChecked: isCompactViewChecked || false,
        defaultViewProps,
        pageSize,
        pageNumber,
        headerRenderers,
        columnRenderers,
        rowDetailsRenderer,
        dataProvider,
        removeFilters,
        hideResetFilters,
        hideHeaderActions,
        hideColumnHeaders,
        hideFooter,
        hasSelectableRows,
        hideGridRefresh,
        hideFiltersToggle,
        disableGridSettings,
        inlineRowActions,
        columnConfigs,
        defaultFlexGrowColumn,
        enableRowClickEvent,
        enableCSVExport,
        showFilters,
        smartViewsEnabled,
        smartViewDiscardButtonDisabledState,
        smartViewSaveButtonDisabledState,
        titleConfig,
        hasPagination,
        pageSizeOptions,
        noEscapeSpaceInTitle,
        sortColumnName,
        sortDirection,
        cellClassNameGenerator,
        hasSelectionExtraOptions,
        filtersUiConfigs,
        favoriteIconConfig,
      } as GridViewMetadata<Column, ColumnKeys>;
    } else if (numEmpty === fields.length) {
      return undefined;
    } else {
      throw new Error('Unexpected: fields should be all missing or all present!');
    }
  }

  private async handleFiltersUpdatedEvent(ev: WebComponentEvent) {
    const newFilters = ev.detail.updatedFilters || [];
    const shouldTriggerDataReload = this.shouldFiltersChangeTriggerDataReload(this.component.filters, newFilters);
    this.component.filters = newFilters;

    if (this.component.smartViewsEnabled) {
      if (isEmpty(this.component.activeViewProps)) {
        throw new Error('Smart views enabled but no active view props set to grid');
      }
      this.component.activeViewProps = {
        ...this.component.activeViewProps,
        filters: newFilters,
      };

      if (this.isViewChanged()) {
        this.enableSmartViewsDiscardButton();
        this.enableSmartViewsSaveButton();
      } else {
        this.disableSmartViewActions();
      }
    } else {
      // the button 'Reset view' is disabled when filters are set to default and default columns are visible
      this.view.dom.$discardViewChanges.disabled =
        isEqual(this.component.defaultViewProps?.filters, this.component.filters) &&
        this.component.columns.length === this.component.defaultViewProps?.displayedColumns.length;
    }

    if (!shouldTriggerDataReload) {
      return;
    }

    this.component.hasPagination ? await this.loadFirstPage() : await this.handleGridItemsLoad();
    this.resetSelection();
    this.selectAllEnabled = false;
    this.view.disableAllFromPage();
  }

  public resetSelection() {
    if (!this.component.hasSelectableRows) {
      return;
    }

    this.selectionStore?.removeAll();
    this.view.dom.$grid.selectedItems = [];
  }

  private shouldColumnsChangeTriggerDataReload(oldColumns: Array<Column>, newColumns: Array<Column>): boolean {
    for (const column of newColumns) {
      if (!oldColumns.includes(column)) {
        return true;
      }
    }

    return false;
  }

  private shouldFiltersChangeTriggerDataReload(
    oldFilters: Array<Filter<ColumnKeys>>,
    newFilters: Array<Filter<ColumnKeys>>
  ): boolean {
    oldFilters = oldFilters.filter(filter => !this.hasDefaultFilterValue(filter));
    newFilters = newFilters.filter(filter => !this.hasDefaultFilterValue(filter));
    return !areArraysEqual(oldFilters, newFilters, sortFiltersByColumnKey);
  }

  private hasDefaultFilterValue(filter: Filter<ColumnKeys>): boolean {
    const filterKey = getColumnKeyForFilter(filter);

    const columnDetails = this.component.allColumnDetails.filter(columnDetail => columnDetail.key === filterKey).pop();

    if (!columnDetails) {
      throw new Error(`Missing column details for key: ${filterKey}`);
    }

    return isDefaultFilter<Column, ColumnKeys>(filter, columnDetails);
  }

  private toggleSideMenu(ev: WebComponentEvent) {
    ev.stopImmediatePropagation();
    const isOpen = this.view.sideMenu.isOpen;
    if (!isOpen) {
      this.view.gridSettings.resetSettingsToInitialState(ev);
      this.view.sideMenu.setMenuContent(this.view.gridSettings);
      this.view.gridSettings.updateEnumListUiState();
      this.view.sideMenu.openSideMenu();
    } else {
      this.view.sideMenu.closeSideMenu();
      this.view.sideMenu.setMenuContent(null);
    }
  }

  private async applySettings(ev: WebComponentEvent) {
    const selectedItems = this.selectionStore
      ? this.selectionStore.getItemsIntersection(this.view.dom.$grid.items)
      : [];
    const settings = {
      ...ev.detail.settings,
      columnConfigs: this.getColumnConfigs(),
    };

    let selectedColumns: Column[] = [];

    if (settings.selectedColumns) {
      selectedColumns = this.orderSelectedColumns(settings.selectedColumns, this.component.columns);
    }
    settings.selectedColumns = selectedColumns;

    await this.updateSettings(settings);
    this.view.sideMenu.closeSideMenu();
    this.view.disableAllGridRowsEditMode();
    /**
     * firstVisibleIndex is private and doesn't have a public getter at the moment
     * in order to avoid the warning for accessing the private firstVisibleIndex, _warnPrivateAPIAccessAsyncEnabled is set to FALSE
     * check file vaadin-grid-scroller.js, method  _warnPrivateAPIAccess(apiName)
     */
    this.view.dom.$grid._warnPrivateAPIAccessAsyncEnabled = false;
    const gridFirstVisibleIndex = this.view.dom.$grid.firstVisibleIndex;

    // Reload the data only if the columns are different than the previous ones.
    const { isCompactViewChecked } = ev.detail.settings as GridSettings<Column>;

    if (selectedColumns.length === 0) {
      throw new Error('Programmatic error: applying settings without columns!');
    }

    this.component.columns = selectedColumns;
    this.component.isCompactViewChecked = isCompactViewChecked;

    const hasRowDetailsRenderer = !isEmpty(this.component.rowDetailsRenderer);

    this.view.buildGridColumns({
      allColumnDetails: this.component.allColumnDetails,
      columns: this.component.columns,
      columnConfigs: this.component.columnConfigs,
      hideColumnHeaders: this.component.hideColumnHeaders,
      hasSelectableRows: this.component.hasSelectableRows,
      inlineRowActions: this.component.inlineRowActions,
      hasPagination: this.component.hasPagination,
      hasSelectionExtraOptions: this.component.hasSelectionExtraOptions,
      hasRowDetails: hasRowDetailsRenderer,
    });

    await this.handleGridItemsLoad().then(() => {
      this.view.dom.$grid.selectedItems = [...selectedItems];
      this.view.dom.$grid.render();
      this.handleRowsLoadingFinished();
    });

    this.view.setGridSettingsColumns(this.component.columns, this.component.allColumnDetails);
    this.view.setCompactMode(ev.detail.settings.isCompactViewChecked);

    if (!isEmpty(gridFirstVisibleIndex)) {
      this.view.dom.$grid.scrollToIndex(gridFirstVisibleIndex);
    }
  }

  private orderSelectedColumns(selectedColumns: Column[], existingOrderedColumns: Column[]) {
    let result: Column[] = [];
    // this array is used for storing ACTION type columns.
    // They will be re-added to the result array after all the other type columns are processed
    const actionColumns: Column[] = [];

    // preserve the existing order

    for (const column of existingOrderedColumns) {
      for (const columnDetails of this.component.allColumnDetails) {
        if (columnDetails.column === column && columnDetails.type === ColumnType.ACTION) {
          actionColumns.push(column);
        }
      }

      if (selectedColumns.includes(column)) {
        result.push(column);
      }
    }

    // take columns that have been added and insert them at the end
    for (const column of selectedColumns) {
      if (!result.includes(column)) {
        result.push(column);
      }
    }

    if (!isEmpty(actionColumns)) {
      result = [...result, ...actionColumns];
    }

    return result;
  }

  private async updateSettings(settings: GridSettings<Column>, errorHandler?: (data: any) => void) {
    if (!errorHandler) {
      errorHandler = console.error;
    }

    if (this.component.smartViewsEnabled) {
      if (isEmpty(this.component.activeViewProps)) {
        throw new Error('Smart views enabled but no active view props set to grid');
      }

      // we update settings only when the save button is pressed
      if (settings.columnConfigs) {
        this.component.activeViewProps.columnConfigs = settings.columnConfigs;
      }

      if (settings.isCompactViewChecked !== undefined) {
        this.component.activeViewProps.compactView = settings.isCompactViewChecked;
      }

      if (settings.pageSize) {
        this.component.activeViewProps.pageSize = settings.pageSize;
      }

      if (settings.selectedColumns) {
        this.component.activeViewProps.displayedColumns = settings.selectedColumns;
      }

      if (this.isViewChanged()) {
        this.enableSmartViewsDiscardButton();
        this.enableSmartViewsSaveButton();
      } else {
        this.disableSmartViewActions();
      }

      this.component.dispatchEvent(new CustomEvent(GridSettingsEvents.SETTINGS_UPDATED, { bubbles: true }));
      return;
    }

    this.view.dom.$discardViewChanges.disabled = false;

    try {
      const response = await this.component.dataProvider.updateSettings({
        viewId: this.component.viewId,
        columns: settings.selectedColumns,
        compactView: settings.isCompactViewChecked,
        pageSize: settings.pageSize,
        pageNumber: settings.pageNumber,
        columnConfigs: settings.columnConfigs,
      });

      /*
        This needs to be set from the response in order to ensure grids that don't save their widths
        don't start saving their widths on the front-end
      */
      if (response.columnConfigs) {
        this.component.columnConfigs = response.columnConfigs;
      }
    } catch (exception) {
      errorHandler(exception);
    }
  }

  private async resetView() {
    let defaultViewProps = this.component.defaultViewProps;

    if (!defaultViewProps) {
      throw new Error('Unexpectedly called resetView when no default view props present.');
    }

    if (this.component.smartViewsEnabled && this.component.activeViewProps.id !== defaultViewProps.id) {
      // if active view is default, no need to reassign the value
      defaultViewProps = this.component.activeViewInitialProps;
    }

    const shouldTriggerDataReload =
      this.component.dataProvider.shouldDataReloadOnResetView() ||
      this.shouldColumnsChangeTriggerDataReload(this.component.columns, defaultViewProps.displayedColumns) ||
      this.shouldFiltersChangeTriggerDataReload(this.component.filters, defaultViewProps.filters);

    this.component.filters = defaultViewProps.filters;
    this.component.isCompactViewChecked = defaultViewProps.compactView;
    this.component.columns = defaultViewProps.displayedColumns;
    this.component.pageSize = defaultViewProps.pageSize;
    this.component.pageNumber = defaultViewProps.pageNumber ?? 1;
    this.component.columnConfigs = defaultViewProps.columnConfigs ? defaultViewProps.columnConfigs : [];
    this.view.resetColumns();
    const data = await this.getDataForView();
    if (!data) {
      throw new Error('Unexpectedly got empty data when reseting view.');
    }
    this.view.buildGrid(data);

    if (shouldTriggerDataReload) {
      this.handleGridItemsLoad().catch(console.error);
    }

    await this.updateSettings({
      selectedColumns: defaultViewProps.displayedColumns,
      isCompactViewChecked: defaultViewProps.compactView,
      pageSize: defaultViewProps.pageSize,
      pageNumber: defaultViewProps.pageNumber,
      columnConfigs: this.getColumnConfigs(),
    });

    const gridFilters: GridFiltersWebComponent<Column, ColumnKeys> = this.view.dom.$gridFilters;
    if (gridFilters && gridFilters.controller && !this.component.removeFilters) {
      (gridFilters.controller as GridFiltersController<Column, ColumnKeys>).setFilters(defaultViewProps.filters);
    }

    const gridFooter: GridFooterWebComponent = this.view.dom.$gridFooter;
    if (gridFooter && gridFooter.controller) {
      (gridFooter.controller as GridFooterController).render();
    }

    this.view.sideMenu.closeSideMenu();
    this.view.dom.$grid.scrollToIndex(0);
    this.disableSmartViewActions();
  }

  private backToTop() {
    this.view.scrollGridToTop();
  }

  private async setPageSize(event: WebComponentEvent) {
    const pageSize: number = event.detail.pageSize;

    this.component.pageSize = pageSize;
    await this.updateSettings({
      pageSize: pageSize,
    });

    if (this.component.hasPagination) {
      await this.loadFirstPage();
    }
  }

  private async setPageNumber(event: WebComponentEvent) {
    this.component.pageNumber = event.detail.pageNumber;
    await this.handleGridItemsLoad();
    if (this.selectionStore) {
      this.view.dom.$grid.selectedItems = this.selectionStore.getItemsIntersection(this.view.dom.$grid.items);
    }
  }

  private async setPageNumberWithoutReloading(event: WebComponentEvent) {
    this.component.pageNumber = event.detail.pageNumber;
    if (this.selectionStore) {
      this.view.dom.$grid.selectedItems = this.selectionStore.getItemsIntersection(this.view.dom.$grid.items);
    }
  }

  private loadMoreWhenScrolledToBottom() {
    if (
      !this.shouldLoadPageOnScroll ||
      this.showingAllItemsOnSinglePage() ||
      this.component.hideFooter ||
      this.component.hasPagination
    ) {
      return;
    }

    const gridTable = this.view.gridTable;

    if (
      Math.ceil(gridTable.scrollTop + gridTable.clientHeight) === gridTable.scrollHeight &&
      gridTable.scrollTop !== 0
    ) {
      this.disableDataLoading(true);
      const pageSize = (this.view.dom.$gridFooter.controller.view as GridFooterView).getPageSize();
      this.loadPageOnInfiniteScroll(pageSize, false).catch(console.error);
    }
  }

  private showingAllItemsOnSinglePage(): boolean {
    return (this.view.dom.$grid.items || []).length % 50 !== 0 || this.noRowsPrevFetched === 0;
  }

  private showingItemsFromLastPage(): boolean {
    const numItemsOnCurrentPage = (this.view.dom.$grid.items || []).length;
    const pageSize = this.component.pageSize;
    const currentPageNumber = Math.max(1, this.component.pageNumber); // current page 0 treated as the first page

    return (currentPageNumber - 1) * pageSize + numItemsOnCurrentPage === this.numItems;
  }

  private showingItemsFromFirstPage(): boolean {
    const currentPageNumber = this.component.pageNumber;
    return currentPageNumber <= 1;
  }

  private setGridViewConfigs() {
    const { sideMenuParent } = this.component;
    if (!sideMenuParent) {
      return;
    }

    this.view.sideMenuParent = sideMenuParent;
    this.gridConfigsSet = true;
  }

  private async handleRowSelectEvent(event: WebComponentEvent) {
    if (this.rowSelectEventRunning) {
      return;
    }

    this.rowSelectEventRunning = true;
    try {
      // Deep copy the array containing the real data of each row, for future use
      const selectedGridItems = JSON.parse(JSON.stringify(this.view.dom.$grid.selectedItems));
      this.component.selectedItems = selectedGridItems;

      await this.handleRowSelectInSelectionStore(event, selectedGridItems);

      this.updateNumSelectedItems();
      if (this.selectAllEnabled) {
        this.selectAllFromCurrentPage();
      }

      this.component.dispatchEvent(
        new CustomEvent(GridEvents.SELECT_ROWS, {
          detail: {
            selectedItemsData: this.component.selectedItems,
            selectionStoreData: this.selectionStore?.storeData,
          },
          bubbles: true,
        })
      );
    } catch (error) {
      // Handle any potential errors
      console.error('Error:', error);
    } finally {
      this.rowSelectEventRunning = false;
    }
  }

  private handleGridTableScrolling() {
    GzTooltip.hide();
    this.component.removeEventListener('wheel', this.handleMouseWheelEvent.bind(this), true);
    this.component.addEventListener('wheel', this.handleMouseWheelEvent.bind(this), true);
  }

  private handleMouseWheelEvent(event: Event) {
    if (this.hasAnyOpenDropdown) {
      event.stopPropagation();
    }
  }

  private handleDropdownOpen(event: Event): void {
    if (event.target instanceof GridCellEditWebComponent) {
      this.hasAnyOpenDropdown = true;
    }
  }

  private handleDropdownClose(event: Event): void {
    if (event.target instanceof GridCellEditWebComponent) {
      this.hasAnyOpenDropdown = false;
    }
  }

  private getColumnConfigs(): GridColumnConfiguration[] {
    const gridColumns = this.view.getGridColumns();

    // the TS compiler throws an error on key because `column.path` might be undefined, but we already filter out
    // columns without a `path` property
    // @ts-ignore
    const drawnColumnConfigs: Array<GridColumnConfiguration> = gridColumns
      .filter((column: GridColumnElement) => column.resizable && column.path)
      .map((column: GridColumnElement) => {
        return { key: column.path, width: column.width };
      });

    const existingGridKeys = drawnColumnConfigs.map(gridConfig => gridConfig.key);

    // We need to merge the existing grid config with the one already set on the component
    const componentPropColumnConfigs = this.component.columnConfigs.filter(
      columnConfig => !existingGridKeys.includes(columnConfig.key)
    );
    return drawnColumnConfigs
      .concat(componentPropColumnConfigs)
      .sort((a, b) => (a.key > b.key ? 1 : b.key > a.key ? -1 : 0));
  }

  private updateGridColumnSizes() {
    this.handleGridColumnsResize();
    this.view.enforceMinColumnWidth();
    const columnConfigs = this.getColumnConfigs();
    // As of now, only the Threats Xplorer grid saves the column widths for persistence.
    // The handling for other grids is made in the error handler
    const handleErrors = (ex: Error) => {
      if (ex.message === 'Not implemented') {
        // Fail silently
        return;
      }

      console.error(ex);
    };

    this.updateSettings({ columnConfigs }, handleErrors);
  }

  private updateGridColumnOrder(activeMovableColumns: GridColumnElement[]) {
    const columns: Column[] = [];
    for (const activeColumn of activeMovableColumns) {
      const allColumnDetails = this.component.allColumnDetails.find(
        colDetails => colDetails.key === activeColumn.get('path')
      );
      if (allColumnDetails) {
        columns.push(allColumnDetails.column);
      }
    }

    this.component.columns = columns;

    const errorHandler = (ex: Error) => {
      if (ex.message === 'Not implemented') {
        // Fail silently
        return;
      }

      console.error(ex);
    };

    this.updateSettings({ selectedColumns: columns }, errorHandler);
  }

  private onEditGridRow(event: WebComponentEvent) {
    const { rowData } = event.detail;
    this.view.enableGridRowEditMode(rowData);

    this.component.dispatchEvent(
      new CustomEvent(GridEvents.EDIT_ROW_ENABLED, {
        detail: event.detail,
      })
    );

    window.addEventListener('resize', this.handleGridColumnsResize.bind(this));
  }

  private onEditGridRowSave(event: WebComponentEvent) {
    const data = event.detail.rowData;
    const newData = this.view.getEditedGridItem();

    const inlineMenu = this.view.getRowInlineMenu(data.index + 2);
    inlineMenu.itemData = newData;

    this.view.saveEditedChanges(data.index);
    this.component.dispatchEvent(
      new CustomEvent(GridEvents.EDIT_ROW_SAVE, {
        detail: {
          newData: newData,
          rowData: data,
        },
      })
    );
  }

  private onEditGridRowCancel(event: WebComponentEvent) {
    const data = event.detail.rowData;
    this.view.discardEditedChanges(data.index);
    this.component.dispatchEvent(new CustomEvent(GridEvents.EDIT_ROW_CANCEL));
    window.removeEventListener('resize', this.handleGridColumnsResize);
  }

  private async loadGridAsync(gridItemsToLoad) {
    if (!this.component.hideFooter) {
      this.view.dom.$gridFooter.controller.updateGridSummaryState(GridSummaryState.LOADING);
      this.view.dom.$gridFooter.gridSummary = '';

      this.fetchAndUpdateSummary().catch(exception => {
        console.error(exception);

        if (exception.message.includes('timed out')) {
          this.view.dom.$gridFooter.gridSummary = this.dependencies.translateFn(
            'Determining the number of items took too long. Please adjust your filters and try again'
          );
          this.view.dom.$gridFooter.controller.updateGridSummaryState(GridSummaryState.WARNING);
          return;
        }
      });
    }

    try {
      await this.fetchAndApplyRowsToGrid(gridItemsToLoad);
    } catch (exception) {
      console.error(exception);
      if (this.view.dom.$wrapper) {
        this.view.setTemplateState(this.view.dom.$wrapper, TemplateState.ERROR);
      }
    }
  }

  private async loadGridSync(gridItemsToLoad) {
    this.view.setElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE);
    try {
      await this.fetchAndUpdateSummary();
      await this.fetchAndApplyRowsToGrid(gridItemsToLoad);
    } finally {
      if (this.view.dom.$wrapper) {
        this.view.removeElementClass(this.view.dom.$wrapper, DATA_LOAD_MORE);
      }
    }
  }

  private handleRowClickEvents(event: WebComponentEvent) {
    if (!this.component.enableRowClickEvent) {
      return;
    }

    for (const target of event.composedPath()) {
      const targetElement = target as HTMLElement;
      if (
        isTargetActionable(target) ||
        ('getAttribute' in targetElement && targetElement?.getAttribute('part') === 'resize-handle')
      ) {
        return;
      }
    }

    const grid = this.view.dom.$grid;
    const item = grid.getEventContext(event).item;
    if (!this.component.hasSelectableRows) {
      grid.selectedItems = item ? [item] : [];
    }

    this.component.dispatchEvent(
      new CustomEvent(GridEvents.ROW_CLICKED, {
        detail: {
          rowDetails: item,
          rowIndex: grid.getEventContext(event).index,
          maxRowIndex: grid.items.length,
          originalEvent: event,
        },
      })
    );

    this.view.highlightGridRowByIndex(grid.getEventContext(event).index);
    this.bodyRef.addEventListener(SideMenuEvents.CLOSE, this.removeHighlight.bind(this));
  }

  public clickOnGridRow(index: number) {
    if (!this.component.enableRowClickEvent) {
      return;
    }

    const grid = this.view.dom.$grid;
    const item = grid.items[index];

    if (!item) {
      return;
    }

    this.component.dispatchEvent(
      new CustomEvent(GridEvents.ROW_CLICKED, {
        detail: {
          rowDetails: item,
          rowIndex: index,
          maxRowIndex: grid.items.length,
        },
      })
    );

    this.view.highlightGridRowByIndex(index);
    this.bodyRef.addEventListener(SideMenuEvents.CLOSE, this.removeHighlight.bind(this));
  }

  private removeHighlight() {
    if (!this.view.dom.$grid) {
      return;
    }

    this.view.removeHighlightGridRows();
    if (this.component.hasSelectableRows) {
      return;
    }

    this.removeAllSelections();
  }

  private onGridSettingsCreated() {
    this.view.dom.$gridSettingsMenuTrigger.addEventListener('click', this.toggleSideMenu.bind(this));
    this.view.gridSettings.addEventListener(
      GridSettingsEvents.SIDE_PANEL_SETTINGS_UPDATED,
      this.applySettings.bind(this)
    );
  }

  private handleCSVExport() {
    this.component.dispatchEvent(
      new CustomEvent(GridEvents.EXPORT_CSV, {
        detail: {
          filters: this.component.filters,
          activeViewId: this.component.activeViewProps.id,
          activeDisplayedColumns: this.component.activeViewProps.displayedColumns,
          sortColumnName: this.component.sortColumnName,
          sortDirection: this.component.sortDirection,
          columns: this.component.columns,
        },
        bubbles: true,
      })
    );
  }

  public updateCsvButtonState(state: GzButtonState, loadingCustomTooltip?: string) {
    switch (state) {
      case GzButtonState.DISABLED:
        this.view.dom.$csvExportButton.showSpinner = false;
        this.view.dom.$csvExportButton.disabled = true;
        this.view.updateExportTooltip(false);
        break;
      case GzButtonState.IS_LOADING:
        this.view.dom.$csvExportButton.showSpinner = true;
        this.view.dom.$csvExportButton.disabled = true;
        this.view.updateExportTooltip(true, loadingCustomTooltip);
        break;
      case GzButtonState.ENABLED:
        this.view.dom.$csvExportButton.showSpinner = false;
        this.view.dom.$csvExportButton.disabled = false;
        this.view.updateExportTooltip(false);
        break;
      default:
        throw new Error(`Unknown button state ${state}`);
    }
    this.view.dom.$exportTooltip.hideTooltip();
  }

  private handleGridSorted(event: WebComponentEvent) {
    const direction = event.detail.direction?.toUpperCase();

    if (direction) {
      this.component.sortColumnName = event.detail.columnName;
      this.component.sortDirection = direction; // ASC or DESC
    } else {
      this.component.sortColumnName = undefined;
      this.component.sortDirection = undefined;
    }

    this.handleGridItemsLoad();
  }

  private handleGridColumnsResize() {
    if (this.view.getEditedGridItem() === null) {
      return;
    }
    this.view.closeActiveEditableEnums();
  }

  private emitShowSaveViewAsDialogEvent() {
    this.component.dispatchEvent(new CustomEvent(SmartViewEvents.SHOW_SAVE_VIEW_AS_DIALOG));
  }

  private emitSmartViewFavoriteChange() {
    this.component.dispatchEvent(new CustomEvent(SmartViewEvents.SMART_VIEW_FAVORITE_CHANGED));
  }

  private emitSaveViewPropsSavedEvent() {
    this.component.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_SAVED, {
        detail: {
          viewConfiguration: this.component.activeViewProps,
        },
        bubbles: true,
      })
    );

    this.disableSmartViewActions();
  }

  private emitToggleFiltersVisibilityEvent() {
    this.component.dispatchEvent(
      new CustomEvent(FilterEvents.TOGGLE_FILTERS_VISIBILITY, {
        detail: {
          showFilters: this.component.showFilters,
        },
        bubbles: true,
      })
    );
  }

  public handleSmartViewChanges(): void {
    if (this.isViewChanged()) {
      this.enableSmartViewsDiscardButton();
      this.enableSmartViewsSaveButton();
    } else {
      this.disableSmartViewActions();
    }
  }

  private enableSmartViewsSaveButton() {
    if (this.component.activeViewProps.type === ViewType.DEFAULT) {
      return;
    }

    this.view.setSaveButtonDisabledState(false);
  }

  private enableSmartViewsDiscardButton() {
    this.view.setDiscardChangesButtonDisabledState(false);
    this.component.smartViewDiscardButtonDisabledState = false;
  }

  public disableSmartViewActions() {
    if (!this.component.smartViewsEnabled) {
      return;
    }

    this.view.setSaveButtonDisabledState(true);
    this.view.setDiscardChangesButtonDisabledState(true);
    this.component.smartViewDiscardButtonDisabledState = true;
  }

  public isViewChanged(): boolean {
    if (!this.component.smartViewsEnabled) {
      return false;
    }

    const activeInitProps = { ...this.component.activeViewInitialProps };
    const activeProps = { ...this.component.activeViewProps };
    return !isEqual(activeProps, activeInitProps);
  }

  public async discardViewChanges() {
    this.view.disableAllGridRowsEditMode();

    if (!this.component.smartViewsEnabled) {
      await this.resetView();
      this.resetColumnsToDefaultWidths();
      this.view.dom.$discardViewChanges.disabled = true;
      return;
    }

    if (!this.component.activeViewInitialProps) {
      throw new Error('Missing active view initial properties field but required for smart views.');
    }
    this.component.activeViewProps = { ...this.component.activeViewInitialProps };
    await this.resetView();
    this.disableSmartViewActions();
    DOMEventDispatcher.dispatchGlobalEvent(this.view.dom.$wrapper, GridEvents.AFTER_DISCARD_CHANGES);
  }

  private async fetchAndApplyRowsToGrid(gridItemsToLoad): Promise<void> {
    this.component.hasPagination
      ? await this.fetchAndOverwriteRowsInGrid()
      : await this.fetchAndAppendRowsToGrid(gridItemsToLoad);
  }

  private handleSelectExtraOptionsChange(event: WebComponentEvent): void {
    switch (event.detail.selected) {
      case SelectionOptions.All_FROM_PAGE:
        this.handleSelectAllFromPageExtraOption();
        break;
      case SelectionOptions.ALL:
        this.view.handleSelectAllExtraOption();
        break;
      default:
        break;
    }
  }

  private handleColumnReorderEvent(e: WebComponentEvent) {
    this.view.collapseOpenRowDetails();
    // Frozen columns, such as the inline menu column, or row selection column are excluded from reordering
    const activeColumns = e.detail.columns.filter((column: GridColumnElement) => !column.frozen) as GridColumnElement[];

    DOMEventDispatcher.dispatchLocalEvent(this.component, GridEvents.COLUMNS_REORDERED, { columns: activeColumns });

    this.updateGridColumnOrder(activeColumns);
  }

  private handleRowsLoadingFinished() {
    this.view.shouldUpdateSelectAllCheckbox(this.component.hasSelectableRows);
    this.view.shouldUpdateSelectionExtraOptions(this.component.hasSelectionExtraOptions);
    if (this.selectionStore) {
      this.view.dom.$grid.selectedItems = this.selectionStore.getItemsIntersection(this.view.dom.$grid.items);
    }
  }

  private async handleSelectAllFromPageExtraOption(): Promise<void> {
    this.selectionStore.removeAll();
    this.selectAllEnabled = false;
    this.selectAllFromCurrentPage();
    await this.updateNumSelectedItems();
  }

  private selectAllFromCurrentPage(): void {
    const selectionColumn: GridSelectionColumnElement | undefined =
      this.view.dom.$grid.querySelector('vaadin-grid-selection-column');

    if (selectionColumn) {
      /**
       * select-all-changed: CustomEvent Fired when the selectAll property changes. Works only on current page
       */
      selectionColumn.selectAll = true;

      this.selectionStore.add(this.view.dom.$grid.items);
    }
  }

  private async updateNumSelectedItems(): Promise<void> {
    if (!this.component.hideFooter) {
      this.view.dom.$gridFooter.numSelectedItems = this.selectAllEnabled
        ? this.view.dom.$gridFooter.numTotalItems
        : this.selectionStore?.count;
      await this.view.dom.$gridFooter.controller?.render(); // re-render the footer

      /**
       * if all the checkboxes from the page are checked, the flag this.selectAllEnabled is true but all the ids are not added in store
       * https://cdn.vaadin.com/vaadin-web-components/23.1.0-alpha1/#/elements/vaadin-grid-selection-column
       */
    }
  }

  private async handleSelectionAll(): Promise<void> {
    this.selectionStore.removeAll();
    this.selectAllEnabled = true;
    this.selectAllFromCurrentPage();

    await this.updateNumSelectedItems();
  }

  public removeAllSelections() {
    if (this.selectionStore) {
      this.selectionStore.removeAll();
    }

    this.view.dom.$grid.selectedItems = [];
  }

  private handleRowSelectInSelectionStore(event: WebComponentEvent, selectedGridItems: Array<GridItem>): void {
    const eventValues = event.detail.value;
    /**
     * verify if instance of selection store exists ( for enableRowClickEvent only )
     */
    if (!this.selectionStore) {
      return;
    }

    /**
     * a checkbox is unchecked by the used and as a result:  - selectAll mode should be disabled
     *                                                       - only the current checked items be present in store
     */
    if (!Array.isArray(event.detail.value) && this.selectAllEnabled) {
      this.selectAllEnabled = false;
      this.selectionStore.removeAll();
      this.selectionStore.add(selectedGridItems as Array<GridItemType>);
      return;
    }

    /**
     * uncheck all items from header ( <vaadin-grid-selection-column> )
     * https://cdn.vaadin.com/vaadin-web-components/23.1.0-alpha1/#/elements/vaadin-grid-selection-column
     */
    if (Array.isArray(eventValues) && isEmpty(eventValues) && this.view.dom.$grid.items.length > 0) {
      if (this.selectAllEnabled) {
        this.selectAllEnabled = false;
        this.selectionStore.removeAll();
        return;
      }
      this.selectionStore.remove(this.view.dom.$grid.items);
      return;
    }

    /**
     * uncheck items from current page one by one
     */
    if (eventValues.indexSplices && eventValues.indexSplices[0].removed.length > 0) {
      this.selectionStore.remove(event.detail.value.indexSplices[0].removed);
    }

    /**
     * add the selected items in store
     */
    if (!this.selectAllEnabled) {
      this.selectionStore.add(selectedGridItems as Array<GridItemType>);
    }
  }

  private async handleRefreshDataFromFooter(): Promise<void> {
    await this.handleGridItemsLoad(true);
    this.view.dom.$gridFilters.controller.render();
  }

  private resetColumnsToDefaultWidths() {
    const columnDefaults: GridColumnPreset[] = [];

    this.component.allColumnDetails.map(columnDetails => {
      const columnConfig = this.component.columnConfigs.find(element => element.key === columnDetails.key);
      columnDefaults.push({
        key: columnDetails.key,
        flexGrow: columnDetails.flexGrow || 1,
        width: columnConfig?.width || columnDetails.width,
      });
    });

    this.view.resetColumnsToDefaultWidths(columnDefaults);
  }

  public updateFavoriteIconConfig(favoriteIconConfig) {
    this.view.dom.$favoriteIcon.icon = favoriteIconConfig.icon;
    this.view.dom.$favoriteIcon.disabled = favoriteIconConfig.disabled;
    toggleActive(this.view.dom.$favoriteIcon, favoriteIconConfig.active);
    this.view.setFavoritesIconTooltipText(favoriteIconConfig);
  }

  private onToggleFiltersBtnClick() {
    this.component.showFilters = !this.component.showFilters;
    this.view.updateFiltersVisibility(this.component.showFilters);
    this.emitToggleFiltersVisibilityEvent();
  }

  public updateCustomStrings() {
    this.view.updateGridWithCustomStrings(this.component.customStrings);
  }

  public getRowByIndex(index) {
    if (index < 0 || index >= this.numItems) {
      return;
    }

    return this.view.dom.$grid.items[index];
  }
}
